<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { messageStamp } from 'shared/helpers/timeHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const messagesGetter = useMapGetter(
  'scheduledWhatsappMessages/getByConversation'
);
const pendingCountGetter = useMapGetter(
  'scheduledWhatsappMessages/getPendingCount'
);
const uiFlags = useMapGetter('scheduledWhatsappMessages/getUIFlags');

const editingId = ref(null);
const nextScheduledAt = ref('');
const isSectionOpen = ref(true);
const previewDialogRef = ref(null);
const previewMessage = ref(null);
let refreshTimer = null;

const messages = computed(() => messagesGetter.value(props.conversationId));
const pendingCount = computed(() =>
  pendingCountGetter.value(props.conversationId)
);
const isBusy = computed(
  () =>
    uiFlags.value.isFetching ||
    uiFlags.value.isUpdating ||
    uiFlags.value.isDeleting
);

const minimumDateTime = computed(() => {
  const minimumTime = new Date(Date.now() + 60000);
  const localTime = new Date(
    minimumTime.getTime() - minimumTime.getTimezoneOffset() * 60000
  );
  return localTime.toISOString().slice(0, 16);
});

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const timestampToLocalInput = timestamp => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  const localTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
};

const statusLabel = status => {
  const labels = {
    pending: t('WHATSAPP_TEMPLATES.SCHEDULE.STATUS.PENDING'),
    sent: t('WHATSAPP_TEMPLATES.SCHEDULE.STATUS.SENT'),
    cancelled: t('WHATSAPP_TEMPLATES.SCHEDULE.STATUS.CANCELLED'),
    failed: t('WHATSAPP_TEMPLATES.SCHEDULE.STATUS.FAILED'),
  };

  return labels[status] || status;
};

const templateName = message => {
  const templateParams =
    message.template_params || message.templateParams || {};
  return (
    message.template_name ||
    message.templateName ||
    templateParams.name ||
    templateParams.template_name ||
    templateParams.template?.name ||
    t('WHATSAPP_TEMPLATES.SCHEDULE.UNKNOWN_TEMPLATE')
  );
};

const templateLanguage = message => {
  const templateParams =
    message.template_params || message.templateParams || {};
  return (
    message.template_language ||
    message.templateLanguage ||
    templateParams.language ||
    templateParams.template?.language ||
    ''
  );
};

const toggleSection = () => {
  isSectionOpen.value = !isSectionOpen.value;
};

const openPreview = message => {
  previewMessage.value = message;
  previewDialogRef.value?.open();
};

const closePreview = () => {
  previewMessage.value = null;
};

const fetchMessages = () => {
  if (!props.conversationId) return;
  store.dispatch('scheduledWhatsappMessages/get', {
    conversationId: props.conversationId,
  });
};

const beginReschedule = message => {
  editingId.value = message.id;
  nextScheduledAt.value = timestampToLocalInput(message.scheduled_at);
};

const clearReschedule = () => {
  editingId.value = null;
  nextScheduledAt.value = '';
};

const rescheduleMessage = async message => {
  if (!nextScheduledAt.value) return;

  try {
    await store.dispatch('scheduledWhatsappMessages/update', {
      conversationId: props.conversationId,
      id: message.id,
      scheduledAt: formatToUTCString(nextScheduledAt.value),
    });
    useAlert(t('WHATSAPP_TEMPLATES.SCHEDULE.RESCHEDULE_SUCCESS'));
    clearReschedule();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('WHATSAPP_TEMPLATES.SCHEDULE.ERROR')
    );
  }
};

const cancelMessage = async message => {
  try {
    await store.dispatch('scheduledWhatsappMessages/delete', {
      conversationId: props.conversationId,
      id: message.id,
    });
    useAlert(t('WHATSAPP_TEMPLATES.SCHEDULE.CANCEL_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('WHATSAPP_TEMPLATES.SCHEDULE.ERROR')
    );
  }
};

onMounted(() => {
  fetchMessages();
  refreshTimer = window.setInterval(fetchMessages, 60000);
});

onBeforeUnmount(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
});

watch(
  () => props.conversationId,
  () => {
    clearReschedule();
    previewDialogRef.value?.close();
    fetchMessages();
  }
);
</script>

<template>
  <section class="px-4 pb-3 border-b border-solid border-n-weak">
    <div class="flex items-center justify-between mb-2">
      <h4 class="m-0 text-sm font-semibold text-n-slate-12">
        {{
          t('WHATSAPP_TEMPLATES.SCHEDULE.SIDEBAR_TITLE', {
            count: pendingCount,
          })
        }}
      </h4>
      <div class="flex items-center gap-1">
        <span
          v-if="isBusy"
          class="i-lucide-loader-circle animate-spin text-n-slate-10"
        />
        <button
          type="button"
          class="inline-flex items-center justify-center w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
          :aria-expanded="isSectionOpen"
          :aria-label="
            isSectionOpen
              ? t('WHATSAPP_TEMPLATES.SCHEDULE.COLLAPSE')
              : t('WHATSAPP_TEMPLATES.SCHEDULE.EXPAND')
          "
          :title="
            isSectionOpen
              ? t('WHATSAPP_TEMPLATES.SCHEDULE.COLLAPSE')
              : t('WHATSAPP_TEMPLATES.SCHEDULE.EXPAND')
          "
          @click="toggleSection"
        >
          <span
            class="size-4"
            :class="isSectionOpen ? 'i-lucide-minus' : 'i-lucide-plus'"
          />
        </button>
      </div>
    </div>

    <div v-show="isSectionOpen" data-testid="scheduled-messages-content">
      <p v-if="!messages.length" class="m-0 text-xs text-n-slate-11">
        {{ t('WHATSAPP_TEMPLATES.SCHEDULE.EMPTY') }}
      </p>

      <ul v-else class="flex flex-col gap-2 p-0 m-0 list-none">
        <li
          v-for="message in messages"
          :key="message.id"
          class="p-2 rounded-lg bg-n-alpha-black2"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="flex items-center min-w-0 gap-1.5">
                <span
                  class="i-lucide-file-text shrink-0 size-3.5 text-n-slate-10"
                />
                <p
                  class="min-w-0 m-0 text-sm font-medium leading-5 truncate text-n-slate-12"
                  :title="templateName(message)"
                >
                  {{ templateName(message) }}
                </p>
              </div>
              <p class="mt-1 mb-0 text-xs text-n-slate-11">
                {{ messageStamp(message.scheduled_at, 'dd/MM/yyyy, HH:mm') }}
              </p>
              <p
                v-if="message.error_reason"
                class="mt-1 mb-0 text-xs text-n-ruby-9 break-words"
              >
                {{ message.error_reason }}
              </p>
            </div>
            <span
              class="shrink-0 px-1.5 py-0.5 text-[11px] rounded bg-n-alpha-2 text-n-slate-11"
            >
              {{ statusLabel(message.status) }}
            </span>
          </div>

          <div class="flex flex-col gap-2 mt-2">
            <div v-if="editingId === message.id" class="flex flex-col gap-2">
              <Input
                v-model="nextScheduledAt"
                type="datetime-local"
                size="sm"
                :min="minimumDateTime"
              />
              <div class="flex justify-end gap-2">
                <NextButton
                  size="xs"
                  faded
                  slate
                  :label="t('WHATSAPP_TEMPLATES.SCHEDULE.CANCEL_EDIT')"
                  @click="clearReschedule"
                />
                <NextButton
                  size="xs"
                  icon="i-lucide-check"
                  :label="t('WHATSAPP_TEMPLATES.SCHEDULE.SAVE')"
                  :disabled="!nextScheduledAt"
                  @click="rescheduleMessage(message)"
                />
              </div>
            </div>
            <div v-else class="flex justify-end gap-2">
              <NextButton
                size="xs"
                faded
                slate
                icon="i-lucide-eye"
                :aria-label="t('WHATSAPP_TEMPLATES.SCHEDULE.VIEW_TEMPLATE')"
                :title="t('WHATSAPP_TEMPLATES.SCHEDULE.VIEW_TEMPLATE')"
                @click="openPreview(message)"
              />
              <NextButton
                v-if="message.status === 'pending'"
                size="xs"
                faded
                slate
                icon="i-lucide-calendar-clock"
                :label="t('WHATSAPP_TEMPLATES.SCHEDULE.RESCHEDULE')"
                @click="beginReschedule(message)"
              />
              <NextButton
                v-if="message.status === 'pending'"
                size="xs"
                faded
                ruby
                icon="i-lucide-trash-2"
                :label="t('WHATSAPP_TEMPLATES.SCHEDULE.CANCEL')"
                @click="cancelMessage(message)"
              />
            </div>
          </div>
        </li>
      </ul>
    </div>

    <Dialog
      ref="previewDialogRef"
      width="md"
      position="top"
      :title="t('WHATSAPP_TEMPLATES.SCHEDULE.PREVIEW_TITLE')"
      :show-cancel-button="false"
      :show-confirm-button="false"
      @close="closePreview"
    >
      <div v-if="previewMessage" class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium uppercase text-n-slate-10">
            {{ t('WHATSAPP_TEMPLATES.SCHEDULE.TEMPLATE_LABEL') }}
          </span>
          <p class="m-0 text-sm font-medium break-words text-n-slate-12">
            {{ templateName(previewMessage) }}
          </p>
          <p
            v-if="templateLanguage(previewMessage)"
            class="m-0 text-xs text-n-slate-11"
          >
            {{ templateLanguage(previewMessage) }}
          </p>
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium uppercase text-n-slate-10">
            {{ t('WHATSAPP_TEMPLATES.PICKER.BODY') }}
          </span>
          <p
            class="max-h-[50vh] p-3 m-0 overflow-y-auto text-sm leading-5 whitespace-pre-wrap rounded-lg bg-n-alpha-black2 text-n-slate-12"
          >
            {{ previewMessage.content }}
          </p>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end">
          <NextButton
            size="sm"
            faded
            slate
            :label="t('WHATSAPP_TEMPLATES.SCHEDULE.CANCEL_EDIT')"
            @click="previewDialogRef?.close()"
          />
        </div>
      </template>
    </Dialog>
  </section>
</template>
